import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';

interface FounderExpiryNoticeProps {
  show: boolean;
  onDismiss: () => void;
}

const copy = {
  en: {
    title: 'Thank you for being a Founding Member 💚',
    body: "You were one of the first to support Say It NOW, and that means a lot to us.\n\nYour pricing has now transitioned to the standard Connection rate. Your Founding Member badge remains as a thank-you for helping shape what this platform has become.\n\nWe're always glad you're here.",
    button: 'Continue',
  },
  es: {
    title: 'Gracias por ser Miembro Fundador 💚',
    body: 'Fuiste uno de los primeros en apoyar Say It NOW, y eso significa mucho para nosotros.\n\nTu precio ha cambiado a la tarifa estándar de Conexión. Tu insignia de Miembro Fundador permanece como agradecimiento por ayudar a dar forma a esta plataforma.\n\nSiempre nos alegra que estés aquí.',
    button: 'Continuar',
  },
  fr: {
    title: 'Merci d\'être Membre Fondateur 💚',
    body: 'Vous avez été parmi les premiers à soutenir Say It NOW, et cela compte beaucoup pour nous.\n\nVotre tarif a été transféré au tarif standard Connection. Votre badge de Membre Fondateur reste en remerciement.\n\nNous sommes toujours heureux de vous avoir.',
    button: 'Continuer',
  },
  pt: {
    title: 'Obrigado por ser Membro Fundador 💚',
    body: 'Você foi um dos primeiros a apoiar o Say It NOW, e isso significa muito para nós.\n\nSeu preço foi transferido para a tarifa padrão Connection. Seu selo de Membro Fundador permanece como agradecimento.\n\nSempre ficamos felizes com sua presença.',
    button: 'Continuar',
  },
  ar: {
    title: 'شكراً لكونك عضواً مؤسساً 💚',
    body: 'كنت من أوائل من دعم Say It NOW، وهذا يعني الكثير لنا.\n\nلقد تم نقل سعرك إلى سعر Connection القياسي. شارة العضو المؤسس تبقى كشكر لمساهمتك.\n\nنحن سعداء دائماً بوجودك.',
    button: 'متابعة',
  },
  ja: {
    title: '創設メンバーでいてくれてありがとうございます 💚',
    body: 'あなたはSay It NOWを最初にサポートしてくれた方の一人であり、それは私たちにとってとても大きな意味があります。\n\n料金は標準のConnectionレートに移行しました。創設メンバーバッジは感謝の印として残ります。\n\nいつもあなたがいてくれて嬉しいです。',
    button: '続ける',
  },
  ko: {
    title: '창립 멤버가 되어 주셔서 감사합니다 💚',
    body: 'Say It NOW를 처음부터 지지해 주신 분이시며, 그것은 저희에게 큰 의미가 있습니다.\n\n가격이 표준 Connection 요금으로 전환되었습니다. 창립 멤버 배지는 감사의 표시로 남아 있습니다.\n\n항상 함께해 주셔서 기쁩니다.',
    button: '계속',
  },
  id: {
    title: 'Terima kasih telah menjadi Anggota Pendiri 💚',
    body: 'Anda adalah salah satu yang pertama mendukung Say It NOW, dan itu berarti banyak bagi kami.\n\nHarga Anda telah beralih ke tarif standar Connection. Lencana Anggota Pendiri tetap sebagai ucapan terima kasih.\n\nKami selalu senang Anda ada di sini.',
    button: 'Lanjutkan',
  },

  hi: {
    title: 'संस्थापक सदस्य होने के लिए धन्यवाद 💚',
    body: "You were one of the first to support Say It NOW, and that means a lot to us.\n\nYour pricing has now transitioned to the standard Connection rate. Your Founding Member badge remains as a thank-you for helping shape what this platform has become.\n\nWe're always glad you're here.",
    button: 'जारी रखें',
  },
};

export function FounderExpiryNotice({ show, onDismiss }: FounderExpiryNoticeProps) {
  const { language } = useLanguage();
  const t = lc(copy, language);
  const [hasBeenShown, setHasBeenShown] = useState(false);

  useEffect(() => {
    if (show && !hasBeenShown) {
      const shown = localStorage.getItem('founder_expiry_shown');
      if (shown) {
        onDismiss();
      }
    }
  }, [show, hasBeenShown, onDismiss]);

  const handleDismiss = () => {
    localStorage.setItem('founder_expiry_shown', 'true');
    setHasBeenShown(true);
    onDismiss();
  };

  const alreadyShown = localStorage.getItem('founder_expiry_shown');
  if (alreadyShown) return null;

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm p-4"
        >
          <Card className="max-w-md w-full">
            <CardContent className="pt-8 pb-6 text-center space-y-4">
              <Heart className="h-10 w-10 text-primary mx-auto" />
              <h2 className="text-xl font-bold text-foreground">{t.title}</h2>
              <p className="text-sm text-muted-foreground whitespace-pre-line leading-relaxed">
                {t.body}
              </p>
              <Button onClick={handleDismiss} className="w-full">
                {t.button}
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
